import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { toast } from '@gitbook/spine-react';

import { shouldSkipPublishDialog, useTrial } from '../../../../../state';
import { apiClient, apiQuery } from '../../../../../state/api';
import { useNavigateToUpgradeScreen } from '../../../../../state/billing/useNavigateToUpgradeScreen';
import { PublishSiteDialog } from './PublishSiteDialog';

export interface UseSitePublishProps {
    organization: api.Organization;
    // For executing extra work, like analytics
    onPress?: () => void;
}

export interface UseSitePublishReturn {
    dialogRootProps: {
        onOpenChange: (isOpen: boolean) => void;
        isOpen?: boolean;
    };
    publishDialogElement: React.ReactElement | null;
    publishSite: (site: api.Site) => Promise<void>;
}

export function useSitePublish(props: UseSitePublishProps): UseSitePublishReturn {
    const { organization, onPress } = props;

    const [currentSite, setCurrentSite] = React.useState<api.Site | null>(null);
    const queryClient = useQueryClient();
    const navigateToUpgradeScreen = useNavigateToUpgradeScreen();
    const trial = useTrial({ organization });
    const isTrialingOrg = trial.status === api.BillingTrialStatus.Active;

    const handlePublishSuccess = (site: api.Site | null) => {
        if (site) {
            setCurrentSite(site);
        }
    };

    const handleDialogClose = (isOpen: boolean) => {
        if (!isOpen) {
            setCurrentSite(null);
        }
    };

    const publishSite = async (site: api.Site) => {
        onPress?.();
        if (shouldSkipPublishDialog(site, organization, isTrialingOrg)) {
            const checkout = await queryClient.fetchQuery(
                apiQuery('previewSitePublishCheckout', {
                    organizationId: organization.id,
                    plan: site.type,
                })
            );

            if (
                checkout.type === 'go-ahead' ||
                checkout.type === 'trial-available' ||
                checkout.type === 'extended-trial-available'
            ) {
                const published =
                    (await toast.catch(async () => {
                        const { data: publishResult } = await apiClient.sites.publishSite(
                            organization.id,
                            site.id
                        );
                        return publishResult as api.Site;
                    }, 'Error while publishing site')) ?? null;

                if (published) {
                    setCurrentSite(published);
                }
                return;
            }

            if (checkout.type === 'checkout') {
                analytics.logEvent(
                    analytics.createOrgEvent(organization, {
                        type: 'organization_open_upgrade',
                        currentFeaturesSet: null,
                        reason: 'checkout_site',
                    })
                );
                navigateToUpgradeScreen({
                    organizationId: organization.id,
                    reason: 'checkout_site',
                    publishSiteId: site.id,
                });
                return;
            }

            throw new Error('unknown checkout type');
        }

        setCurrentSite(site);
    };

    return {
        dialogRootProps: { onOpenChange: handleDialogClose, isOpen: currentSite !== null },
        publishDialogElement: (
            <PublishSiteDialog
                organization={organization}
                site={currentSite}
                onPublishSuccess={handlePublishSuccess}
            />
        ),
        publishSite,
    };
}
